import React, { useState, useEffect, useCallback } from "react";
import {
  Anchor,
  Waves,
  Sun,
  Mountain,
  Leaf,
  Ship,
  ImagePlus,
  Cross,
  Compass,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { useI18n } from "@/contexts/I18nContext";
import SjdsCrossPromo from "@/components/SjdsCrossPromo";
import DigitalNomadPromo from "@/components/DigitalNomadPromo";
import TsunamiSpotlight from "@/components/TsunamiSpotlight";
import { OVERVIEW, BODIES } from "@/data/historyContent";

/**
 * History page content for Popoyo.app
 * Articles supplied by the Popoyo team. Each <Article> can hold its own
 * photos via the `photos` prop. When `photos` is empty the card shows
 * a soft "Photo coming soon" slot so the editor knows where the image
 * will land once it is sent.
 */

const HERO_IMG = "/history/coast-panorama.jpg";

const PHOTOS = {
  // Present-day photography of the Popoyo / Tola coast — license-free imagery
  // (Unsplash / Pexels), self-hosted under /history so the gallery can never
  // break. Captions describe each scene honestly: no fabricated dates or
  // invented "archive" provenance (verified vintage Popoyo photos are not
  // publicly available — the area's history is largely oral).
  coastPanorama: { id: "coastPanorama", src: "/history/coast-panorama.jpg", caption: "The Emerald Coast at Popoyo — the Tola shoreline today", credit: "Popoyo.app" },
  pangaSand:     { id: "pangaSand", src: "/history/panga-sand.jpg", caption: "An artisanal panga on the sand — the fishing tradition that built this coast", credit: "Popoyo.app" },
  fishingBoats:  { id: "fishingBoats", src: "/history/fishing-boats.jpg", caption: "Pangas drawn up on the beach — the working heart of a Tola fishing village", credit: "Popoyo.app" },
  pangaLaunch:   { id: "pangaLaunch", src: "/history/panga-launch.jpg", caption: "Launching a panga through the shorebreak at dawn", credit: "Popoyo.app" },
  cattleRoad:    { id: "cattleRoad", src: "/history/cattle-road.jpg", caption: "Cattle on a Tola back road — the ranching country behind the beaches", credit: "Popoyo.app" },
  surferSunset:  { id: "surferSunset", src: "/history/surfer-sunset.jpg", caption: "A lone surfer at sunset off the Rivas coast — the waves that changed everything", credit: "Popoyo.app" },
  beachFoliage:  { id: "beachFoliage", src: "/history/beach-foliage.jpg", caption: "Empty sand framed by tropical foliage — the coast rebuilt south of Playa Sardinas after 1992", credit: "Popoyo.app" },
  surfersWave:   { id: "surfersWave", src: "/history/surfers-wave.jpg", caption: "Barrels along the Emerald Coast reefs — Colorado & Panga Drops country", credit: "Popoyo.app" },
  aerialWaves:   { id: "aerialWaves", src: "/history/aerial-waves.jpg", caption: "Lines of Pacific swell at sunset — Popoyo's year-round offshore glass", credit: "Popoyo.app" },
};

// Localized captions per photo id (English lives in PHOTOS[].caption).
const CAPTIONS = {
  coastPanorama: {
    es: "La Costa Esmeralda en Popoyo — el litoral de Tola hoy",
    fr: "La Costa Esmeralda à Popoyo — le littoral de Tola aujourd'hui",
    de: "Die Esmeralda-Küste bei Popoyo — die Küste von Tola heute",
    zh: "波波约的翡翠海岸——今日托拉海岸线",
    it: "La Costa Esmeralda a Popoyo — il litorale di Tola oggi",
    ru: "Изумрудный берег в Попойо — побережье Толы сегодня",
  },
  pangaSand: {
    es: "Una panga artesanal en la arena — la tradición pesquera que construyó esta costa",
    fr: "Une panga artisanale sur le sable — la tradition de pêche qui a bâti cette côte",
    de: "Eine handwerkliche Panga im Sand — die Fischertradition, die diese Küste prägte",
    zh: "沙滩上的传统小渔船——塑造这片海岸的渔业传统",
    it: "Una panga artigianale sulla sabbia — la tradizione di pesca che ha costruito questa costa",
    ru: "Кустарная панга на песке — рыбацкая традиция, создавшая это побережье",
  },
  fishingBoats: {
    es: "Pangas varadas en la playa — el corazón de un pueblo pesquero de Tola",
    fr: "Des pangas tirées sur la plage — le cœur d'un village de pêcheurs de Tola",
    de: "Auf den Strand gezogene Pangas — das Herz eines Fischerdorfs in Tola",
    zh: "拉上海滩的渔船——托拉渔村的核心",
    it: "Pangas tirate in secco sulla spiaggia — il cuore di un villaggio di pescatori di Tola",
    ru: "Панги, вытащенные на берег — сердце рыбацкой деревни Толы",
  },
  pangaLaunch: {
    es: "Botando una panga entre las olas al amanecer",
    fr: "Mise à l'eau d'une panga dans les vagues à l'aube",
    de: "Eine Panga bei Sonnenaufgang durch die Brandung zu Wasser lassen",
    zh: "黎明时分穿越碎浪推船出海",
    it: "Varo di una panga tra le onde all'alba",
    ru: "Спуск панги через прибой на рассвете",
  },
  cattleRoad: {
    es: "Ganado en un camino de Tola — la tierra ganadera detrás de las playas",
    fr: "Du bétail sur un chemin de Tola — le pays d'élevage derrière les plages",
    de: "Vieh auf einem Feldweg in Tola — das Weideland hinter den Stränden",
    zh: "托拉小路上的牛群——海滩背后的牧场之乡",
    it: "Bestiame su una strada di Tola — la terra d'allevamento dietro le spiagge",
    ru: "Скот на просёлке в Толе — животноводческий край за пляжами",
  },
  surferSunset: {
    es: "Un surfista solitario al atardecer en la costa de Rivas — las olas que lo cambiaron todo",
    fr: "Un surfeur solitaire au coucher du soleil sur la côte de Rivas — les vagues qui ont tout changé",
    de: "Ein einsamer Surfer bei Sonnenuntergang vor der Küste von Rivas — die Wellen, die alles veränderten",
    zh: "里瓦斯海岸日落时分的孤独冲浪者——改变一切的海浪",
    it: "Un surfista solitario al tramonto sulla costa di Rivas — le onde che hanno cambiato tutto",
    ru: "Одинокий сёрфер на закате у побережья Риваса — волны, изменившие всё",
  },
  beachFoliage: {
    es: "Arena vacía enmarcada por vegetación tropical — la costa reconstruida al sur de Playa Sardinas tras 1992",
    fr: "Sable vide encadré de végétation tropicale — la côte reconstruite au sud de Playa Sardinas après 1992",
    de: "Leerer Sand, umrahmt von tropischem Grün — die nach 1992 südlich von Playa Sardinas wiederaufgebaute Küste",
    zh: "热带植被环绕的空旷沙滩——1992 年后在萨尔迪纳斯海滩以南重建的海岸",
    it: "Sabbia deserta incorniciata dalla vegetazione tropicale — la costa ricostruita a sud di Playa Sardinas dopo il 1992",
    ru: "Пустой песок в обрамлении тропической зелени — побережье, отстроенное к югу от Плайя-Сардинас после 1992 года",
  },
  surfersWave: {
    es: "Tubos en los reefs de la Costa Esmeralda — territorio de Colorado y Panga Drops",
    fr: "Des tubes sur les reefs de la Costa Esmeralda — le pays de Colorado et Panga Drops",
    de: "Barrels an den Riffen der Costa Esmeralda — das Revier von Colorado und Panga Drops",
    zh: "翡翠海岸礁石上的管浪——科罗拉多与 Panga Drops 之地",
    it: "Tubi sui reef della Costa Esmeralda — terra di Colorado e Panga Drops",
    ru: "Трубы на рифах Косты-Эсмеральды — край Колорадо и Панга-Дропс",
  },
  aerialWaves: {
    es: "Líneas de marejada del Pacífico al atardecer — el cristal offshore de Popoyo todo el año",
    fr: "Lignes de houle du Pacifique au coucher du soleil — le vitreux offshore de Popoyo toute l'année",
    de: "Pazifische Swell-Linien bei Sonnenuntergang — Popoyos ganzjähriges Offshore-Glas",
    zh: "日落时太平洋的涌浪线——波波约全年的离岸镜面浪",
    it: "Linee di mare del Pacifico al tramonto — il vetro offshore di Popoyo tutto l'anno",
    ru: "Линии тихоокеанского свелла на закате — офшорное «стекло» Попойо круглый год",
  },
};

// Resolve a photo's caption for the active language (English fallback).
const capOf = (photo, lang) =>
  (lang && lang !== "en" && photo?.id && CAPTIONS[photo.id]?.[lang]) || photo?.caption || "";

// Localized heading for the photo gallery block.
const GALLERY_LABEL = {
  en: "Popoyo & Tola in Photos",
  es: "Popoyo y Tola en fotos",
  fr: "Popoyo et Tola en images",
  de: "Popoyo & Tola in Bildern",
  zh: "图说波波约与托拉",
  it: "Popoyo e Tola in foto",
  ru: "Попойо и Тола в фотографиях",
};

// Article-level aliases keep each story's photo reference stable while pointing
// at the correct present-day image for its theme.
PHOTOS.historyHero  = PHOTOS.coastPanorama;   // overview
PHOTOS.bigCatch     = PHOTOS.pangaSand;        // fishing & farming roots
PHOTOS.town1940s    = PHOTOS.cattleRoad;       // land, ranching & reform
PHOTOS.warehouseBay = PHOTOS.beachFoliage;     // 1992 tsunami & rebuilding
PHOTOS.beachCar     = PHOTOS.surferSunset;     // surf-lodge era
PHOTOS.valleyView   = PHOTOS.aerialWaves;      // Emerald Coast today

// Order in which the gallery slider presents the photos (no duplicates).
const GALLERY_ORDER = [
  "coastPanorama",
  "pangaSand",
  "fishingBoats",
  "pangaLaunch",
  "cattleRoad",
  "surferSunset",
  "surfersWave",
  "beachFoliage",
  "aerialWaves",
];

/**
 * Archive photo gallery slider. One large photo at a time with prev/next
 * controls, a counter, a horizontal thumbnail strip, and arrow-key support.
 * Photos auto-advance every 6 s when the carousel is not focused / hovered.
 */
function ArchiveGallery() {
  const { lang } = useI18n();
  const slides = GALLERY_ORDER.map((k) => PHOTOS[k]).filter(Boolean);
  const [i, setI] = useState(0);
  const [paused, setPaused] = useState(false);

  const go = useCallback((next) => {
    setI((c) => (next + slides.length) % slides.length);
  }, [slides.length]);

  // Auto-advance every 6s unless the user is hovering / interacting.
  useEffect(() => {
    if (paused) return undefined;
    const t = setInterval(() => go(i + 1), 6000);
    return () => clearInterval(t);
  }, [i, paused, go]);

  // Keyboard navigation (← / →)
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "ArrowLeft") go(i - 1);
      else if (e.key === "ArrowRight") go(i + 1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [i, go]);

  if (!slides.length) return null;
  const active = slides[i];
  // Previous index — drawn underneath while the new slide fades in on top,
  // producing a smooth cross-fade instead of a hard swap.
  const prevI = (i - 1 + slides.length) % slides.length;
  const prev = slides[prevI];

  return (
    <section
      data-testid="history-gallery"
      className="clay overflow-hidden"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onTouchStart={() => setPaused(true)}
    >
      <div className="px-4 sm:px-6 pt-4 sm:pt-5 flex items-baseline justify-between">
        <p className="eyebrow text-[color:var(--coral)]">{GALLERY_LABEL[lang] || GALLERY_LABEL.en}</p>
        <span className="text-xs text-[color:var(--ink-2)] tabular-nums">
          {i + 1} / {slides.length}
        </span>
      </div>

      <div className="relative mt-3 bg-black/5">
        <div
          className="relative w-full overflow-hidden"
          style={{ aspectRatio: "16 / 10", maxHeight: 560 }}
        >
          {/* Bottom layer — fades OUT as the new slide settles in */}
          <img
            key={`prev-${prev.src}`}
            src={prev.src}
            alt=""
            aria-hidden
            decoding="async"
            className="absolute inset-0 w-full h-full object-contain bg-[color:var(--sand-2)] popoyo-gallery-prev"
          />
          {/* Active layer — fades and scales IN gently for a smooth transition */}
          <img
            key={`active-${active.src}`}
            src={active.src}
            alt={active.caption}
            data-testid={`gallery-slide-${i}`}
            loading="eager"
            decoding="async"
            className="absolute inset-0 w-full h-full object-contain bg-[color:var(--sand-2)] popoyo-gallery-active"
          />
        </div>

        {/* Prev / Next controls */}
        <button
          type="button"
          aria-label="Previous photo"
          data-testid="gallery-prev"
          onClick={() => go(i - 1)}
          className="absolute left-2 sm:left-4 top-1/2 -translate-y-1/2 grid place-items-center w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-white/85 hover:bg-white text-[color:var(--ink)] shadow-md transition"
        >
          <ChevronLeft className="w-5 h-5 sm:w-6 sm:h-6" />
        </button>
        <button
          type="button"
          aria-label="Next photo"
          data-testid="gallery-next"
          onClick={() => go(i + 1)}
          className="absolute right-2 sm:right-4 top-1/2 -translate-y-1/2 grid place-items-center w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-white/85 hover:bg-white text-[color:var(--ink)] shadow-md transition"
        >
          <ChevronRight className="w-5 h-5 sm:w-6 sm:h-6" />
        </button>
      </div>

      <div className="px-4 sm:px-6 py-3">
        <p className="text-sm sm:text-base font-semibold text-[color:var(--ink)]" style={{ fontFamily: "Outfit" }}>
          {capOf(active, lang)}
        </p>
        <p className="text-[11px] uppercase tracking-widest text-[color:var(--ink-2)] mt-0.5">
          {active.credit}
        </p>
      </div>

      {/* Thumbnail strip */}
      <div className="px-3 sm:px-5 pb-4 sm:pb-5 overflow-x-auto thin-scroll">
        <div className="flex gap-2 min-w-max">
          {slides.map((s, idx) => (
            <button
              key={s.src}
              type="button"
              aria-label={`${idx + 1}: ${capOf(s, lang)}`}
              data-testid={`gallery-thumb-${idx}`}
              onClick={() => setI(idx)}
              className={`relative shrink-0 w-16 h-16 sm:w-20 sm:h-20 rounded-xl overflow-hidden ring-2 transition ${idx === i ? "ring-[color:var(--coral)]" : "ring-transparent hover:ring-[color:var(--ink-2)]/40"}`}
            >
              <img
                src={s.src}
                alt=""
                loading="lazy"
                decoding="async"
                className="w-full h-full object-cover"
              />
            </button>
          ))}
        </div>
      </div>
    </section>
  );
}

function PhotoGrid({ photos }) {
  const { lang } = useI18n();
  if (!photos || photos.length === 0) return null;
  return (
    <div
      data-testid="article-photos"
      className={`mt-5 grid gap-3 ${photos.length === 1 ? "grid-cols-1" : photos.length === 2 ? "grid-cols-1 sm:grid-cols-2" : "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3"}`}
    >
      {photos.map((p, i) => (
        <figure
          key={i}
          data-testid={`article-photo-${i}`}
          className="rounded-2xl overflow-hidden bg-[color:var(--sand-2)] group"
        >
          <div className="aspect-[4/3] overflow-hidden">
            <img
              src={p.src}
              alt={capOf(p, lang)}
              loading="lazy"
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
            />
          </div>
          <figcaption className="px-3 py-2">
            <p className="text-xs font-semibold leading-snug" style={{ fontFamily: "Outfit" }}>{capOf(p, lang)}</p>
            <p className="text-[10px] uppercase tracking-widest text-[color:var(--ink-2)] mt-0.5">{p.credit}</p>
          </figcaption>
        </figure>
      ))}
    </div>
  );
}

function PhotoPlaceholder({ label }) {
  return (
    <div
      data-testid="article-photo-placeholder"
      className="mt-5 rounded-2xl border-2 border-dashed border-[color:var(--ink-2)]/25 bg-[color:var(--sand-2)]/40 p-6 flex items-center gap-3 text-[color:var(--ink-2)]"
    >
      <ImagePlus className="w-5 h-5 shrink-0" />
      <p className="text-sm leading-snug">{label}</p>
    </div>
  );
}

function Article({ id, icon: Icon, title, children, photos, photoPlaceholder, customBody }) {
  return (
    <article id={id} data-testid={`article-${id}`} className="clay p-6 md:p-8 scroll-mt-24">
      <div className="flex items-start gap-3 mb-3">
        <div className="w-11 h-11 rounded-2xl bg-[color:var(--coral)]/10 text-[color:var(--coral)] flex items-center justify-center shrink-0">
          <Icon className="w-5 h-5" />
        </div>
        <h2 className="text-2xl md:text-3xl font-bold mt-1" style={{ fontFamily: "Outfit" }}>{title}</h2>
      </div>
      <div className="prose prose-sm sm:prose-base max-w-none text-[color:var(--ink)] leading-relaxed space-y-3">
        {customBody || children}
      </div>
      <PhotoGrid photos={photos} />
      {(!photos || photos.length === 0) && photoPlaceholder && (
        <PhotoPlaceholder label={photoPlaceholder} />
      )}
    </article>
  );
}

function H3({ children }) {
  return (
    <h3
      className="text-xl md:text-2xl font-bold mt-6 mb-2 text-[color:var(--navy)]"
      style={{ fontFamily: "Outfit" }}
    >
      {children}
    </h3>
  );
}

// Renders the localized overview article from structured data (all languages).
function OverviewRenderer({ data }) {
  if (!data) return null;
  return (
    <>
      <p>{data.intro}</p>
      {data.sections.map((s, i) => (
        <React.Fragment key={i}>
          <H3>{s.h}</H3>
          <p>{s.p}</p>
        </React.Fragment>
      ))}
      {data.quote && (
        <p className="italic text-[color:var(--ink-2)] border-l-2 border-[color:var(--coral)] pl-4 mt-4">
          {"\u201C"}{data.quote}{"\u201D"}
        </p>
      )}
    </>
  );
}

const PopoyoOverviewBody = (
  <>
    <p>
      Popoyo sits on the Pacific coast of the Tola municipality, in Nicaragua’s
      Rivas department. Once a remote stretch of fishing hamlets and farmland, it
      has become one of Central America’s most celebrated surf destinations —
      famous for a world-class reef break and for offshore winds, generated by
      nearby Lake Nicaragua, that groom the waves clean nearly year-round.
    </p>

    <H3>Fishing, Farming & Indigenous Roots</H3>
    <p>
      For generations this coast was defined by small-scale artisanal fishing and
      subsistence farming. Communities around the Nahualapa River and the salt
      flats of Las Salinas lived by the tides, launching pangas from the sand and
      working the land inland. That quiet, self-reliant rhythm shaped the culture
      travelers still feel in Popoyo today.
    </p>

    <H3>Revolution & Land Reform</H3>
    <p>
      After the 1979 revolution, land across southern Tola was redistributed
      through agrarian reform to local cooperatives. For over a decade the coast
      remained rural and largely off the map — reachable only by rough dirt tracks,
      with no electricity grid or paved roads to speak of.
    </p>

    <H3>The 1990 Turn</H3>
    <p>
      The 1990 election of Violeta Chamorro and the neoliberal policies that
      followed opened the door for private land purchases. A trickle of foreign
      investors and traveling surfers began arriving, drawn by empty perfect waves
      and cheap coastal land — the first hint of the transformation to come.
    </p>

    <H3>The 1992 Tsunami</H3>
    <p>
      Popoyo’s modern history was shaped by the September 1992 Pacific tsunami,
      which struck the Nicaraguan coast and destroyed nearly all of the original
      village. Survivors rebuilt, relocating the community to higher, safer ground
      to the south of Playa Sardinas — the layout that defines Popoyo and Guasacate
      today.
    </p>

    <H3>The Birth of Surf Tourism</H3>
    <p>
      Surf tourism took off in the late 1990s. The 1999 opening of the Popoyo Surf
      Lodge — the first all-inclusive surf camp in the country — put the break on
      the international map and shifted the local economy from fishing toward a
      service world of camps, hostels, restaurants and guides.
    </p>

    <H3>Roads, Bridges & the Emerald Coast</H3>
    <p>
      Infrastructure in the early 2000s — a bridge over the Nahualapa River and new
      roads out to the Guasacate peninsula — accelerated everything. Today Popoyo is
      the heart of the “Emerald Coast” (Costa Esmeralda): Playa Popoyo to the south,
      Playa Guasacate to the north, and a string of world-class breaks from Colorado
      and Panga Drops to El Astillero.
    </p>

    <H3>Popoyo Today</H3>
    <p>
      Popoyo is now a lively blend of Nicaraguan fishing families, surfers, digital
      nomads and expat-owned businesses. It remains refreshingly raw — largely
      cash-only, with no ATMs in the village and dirt roads that still call for a
      4×4 — even as it navigates real questions around land, water and the balance
      between local life and a growing tourism economy.
    </p>

    <p className="italic text-[color:var(--ink-2)] border-l-2 border-[color:var(--coral)] pl-4 mt-4">
      “Where offshore winds, world-class waves, and a proud fishing coast come together.”
    </p>
  </>
);

const ARTICLES = [
  {
    id: "popoyo-overview",
    icon: Compass,
    title: "The History of Popoyo, Nicaragua",
    customBody: PopoyoOverviewBody,
    photos: [PHOTOS.historyHero],
  },
  {
    id: "tola-roots",
    icon: Waves,
    title: "Fishing, Farming & Indigenous Roots",
    body: [
      "Long before the surfers arrived, the Tola coast around Popoyo lived by fishing and farming. Families launched wooden pangas straight off the beach at dawn and worked small plots of land inland, trading fish, salt and produce in the villages of Las Salinas de Nahualapa and El Astillero.",
      "The Nahualapa River and the coastal salt flats shaped daily life. Salt production, subsistence agriculture and artisanal fishing were the backbone of the local economy for generations, long into the twentieth century.",
      "This was a rural periphery — remote, self-reliant and largely disconnected from Nicaragua’s cities. That isolation preserved both the empty coastline and the tight-knit community spirit that still defines the area today.",
    ],
    photos: [PHOTOS.bigCatch],
  },
  {
    id: "land-revolution",
    icon: Mountain,
    title: "Revolution, Land Reform & the 1990 Turn",
    body: [
      "The 1979 Sandinista revolution reached even this quiet coast. Through agrarian reform, land across southern Tola was redistributed to local cooperatives, and for more than a decade the region stayed rural, roadless and off the tourist map.",
      "Everything shifted with the 1990 election of Violeta Chamorro. The neoliberal policies that followed made it possible for private buyers — including foreign investors and traveling surfers — to purchase coastal property.",
      "That change set the stage for Popoyo’s transformation. What had been cooperative farmland and empty beaches slowly began attracting outside capital drawn by perfect, uncrowded waves.",
    ],
    photos: [PHOTOS.town1940s],
  },
  {
    id: "tsunami-1992",
    icon: Anchor,
    title: "The 1992 Tsunami & Rebuilding the Village",
    body: [
      "On the night of September 1, 1992, a powerful earthquake off the Pacific coast sent a tsunami crashing into Nicaragua’s shoreline. The waves devastated coastal communities from the Rivas department northward.",
      "In Popoyo, the tsunami destroyed nearly all of the original village. It remains the single most defining event in the town’s modern history.",
      "Survivors rebuilt on higher, safer ground to the south of Playa Sardinas. That relocation created the layout of Popoyo and neighboring Guasacate that visitors know today — a resilient community shaped by the sea in every sense.",
    ],
    photos: [PHOTOS.warehouseBay],
  },
  {
    id: "surf-lodge",
    icon: Sun,
    title: "Popoyo Surf Lodge & the Birth of Surf Tourism",
    body: [
      "Traveling surfers had been scoring Popoyo’s reef since the early 1990s, but the modern surf scene truly began in 1999 with the opening of the Popoyo Surf Lodge — the first all-inclusive surf camp in Nicaragua.",
      "The lodge put Popoyo on the international surf map and proved a whole economy could be built around the waves. Fishing families increasingly turned to guiding, hospitality, transport and food service for visiting surfers.",
      "Over the next two decades, camps, hostels, yoga retreats and restaurants multiplied across Popoyo and Guasacate — while the offshore-groomed A-frame reef remained the reason everyone came in the first place.",
    ],
    photos: [PHOTOS.beachCar],
  },
  {
    id: "colorado-iguana",
    icon: Leaf,
    title: "Playa Colorado, Panga Drops & Hacienda Iguana",
    body: [
      "A short drive from Popoyo, the Hacienda Iguana area holds two of the region’s most famous waves. Playa Colorado is a fast, hollow river-mouth beach break that barrels on a solid swell — one of the best beach breaks in Central America.",
      "Just offshore, Panga Drops is a deep-water reef that stays consistent and holds size when other spots close out, its long rights a favorite among visiting surfers.",
      "Once quiet ranchland, Hacienda Iguana grew into a gated residential and resort community in the 2000s. The beaches themselves remain public, and the waves out front helped cement the Emerald Coast’s world-class reputation.",
    ],
    photos: [PHOTOS.surfersWave],
  },
  {
    id: "emerald-coast",
    icon: Ship,
    title: "El Astillero & the Emerald Coast Today",
    body: [
      "North of Popoyo, El Astillero endures as a working fishing village — pangas on the sand, the day’s catch sold at the shore — while a river-mouth break out front can serve up high-quality barrels for those who make the trip.",
      "Together with Gigante to the south, these towns form Nicaragua’s “Emerald Coast” (Costa Esmeralda): a chain of surf villages, boutique lodges and empty beaches strung along the Tola shoreline.",
      "Popoyo today balances its fishing-village soul with a booming surf-tourism economy. It stays wonderfully raw — cash-only, dirt roads, 4×4 country — even as the community works through real questions of land tenure, water security and sustainable growth for the generations to come.",
    ],
    photos: [PHOTOS.valleyView],
  },
];

const ARTICLE_TITLE_KEYS = {
  "popoyo-overview": "hist_overview_title",
  "tola-roots": "hist_roots_title",
  "land-revolution": "hist_reform_title",
  "tsunami-1992": "hist_tsunami_title",
  "surf-lodge": "hist_surflodge_title",
  "colorado-iguana": "hist_colorado_title",
  "emerald-coast": "hist_emerald_title",
};

const BODIES_BY_LANG = {
  es: {
    "tola-roots": [
      "Mucho antes de que llegaran los surfistas, la costa de Tola alrededor de Popoyo vivía de la pesca y la agricultura. Las familias lanzaban pangas de madera desde la playa al amanecer y trabajaban pequeñas parcelas tierra adentro, comerciando pescado, sal y productos en los pueblos de Las Salinas de Nahualapa y El Astillero.",
      "El río Nahualapa y las salinas costeras marcaban la vida diaria. La producción de sal, la agricultura de subsistencia y la pesca artesanal fueron la columna vertebral de la economía local durante generaciones, hasta bien entrado el siglo XX.",
      "Era una periferia rural — remota, autosuficiente y en gran medida desconectada de las ciudades de Nicaragua. Ese aislamiento preservó tanto la costa vacía como el fuerte espíritu comunitario que aún define la zona.",
    ],
    "land-revolution": [
      "La revolución sandinista de 1979 llegó incluso a esta costa tranquila. Mediante la reforma agraria, la tierra del sur de Tola se redistribuyó a cooperativas locales, y durante más de una década la región siguió siendo rural, sin caminos y fuera del mapa turístico.",
      "Todo cambió con la elección de Violeta Chamorro en 1990. Las políticas neoliberales que siguieron hicieron posible que compradores privados — incluidos inversionistas extranjeros y surfistas viajeros — adquirieran propiedades costeras.",
      "Ese cambio preparó el terreno para la transformación de Popoyo. Lo que había sido tierra cooperativa y playas vacías empezó poco a poco a atraer capital externo, seducido por olas perfectas y sin gente.",
    ],
    "tsunami-1992": [
      "La noche del 1 de septiembre de 1992, un fuerte terremoto frente a la costa del Pacífico envió un tsunami que golpeó el litoral de Nicaragua. Las olas devastaron comunidades costeras desde el departamento de Rivas hacia el norte.",
      "En Popoyo, el tsunami destruyó casi todo el pueblo original. Sigue siendo el acontecimiento más determinante de la historia moderna del lugar.",
      "Los sobrevivientes reconstruyeron en terreno más alto y seguro, al sur de Playa Sardinas. Esa reubicación creó la disposición de Popoyo y de la vecina Guasacate que los visitantes conocen hoy — una comunidad resiliente moldeada por el mar en todos los sentidos.",
    ],
    "surf-lodge": [
      "Los surfistas viajeros ya disfrutaban del reef de Popoyo desde inicios de los años noventa, pero la escena moderna del surf comenzó de verdad en 1999 con la apertura del Popoyo Surf Lodge — el primer campamento de surf con todo incluido de Nicaragua.",
      "El lodge puso a Popoyo en el mapa internacional del surf y demostró que se podía construir toda una economía alrededor de las olas. Las familias pescadoras se volcaron cada vez más al guiado, la hostelería, el transporte y la gastronomía para los surfistas.",
      "En las dos décadas siguientes, campamentos, hostales, retiros de yoga y restaurantes se multiplicaron por Popoyo y Guasacate — mientras el reef A-frame peinado por el viento offshore seguía siendo la razón por la que todos venían.",
    ],
    "colorado-iguana": [
      "A poca distancia de Popoyo, la zona de Hacienda Iguana alberga dos de las olas más famosas de la región. Playa Colorado es un beach break rápido y hueco en desembocadura de río que entuba con un buen swell — uno de los mejores beach breaks de Centroamérica.",
      "Justo mar adentro, Panga Drops es un reef de aguas profundas que se mantiene consistente y aguanta el tamaño cuando otros spots cierran; sus largas derechas son favoritas de los surfistas.",
      "Antes tierra de haciendas, Hacienda Iguana se convirtió en una comunidad residencial y de resort cerrada en los años 2000. Las playas siguen siendo públicas, y las olas frente a ellas ayudaron a consolidar la reputación de clase mundial de la Costa Esmeralda.",
    ],
    "emerald-coast": [
      "Al norte de Popoyo, El Astillero perdura como un pueblo pesquero en activo — pangas en la arena, la pesca del día vendida en la orilla — mientras una ola de desembocadura frente al pueblo puede regalar tubos de calidad a quienes hacen el viaje.",
      "Junto con Gigante al sur, estos pueblos forman la “Costa Esmeralda” de Nicaragua: una cadena de pueblos surfistas, lodges boutique y playas vacías a lo largo del litoral de Tola.",
      "Popoyo hoy equilibra su alma de pueblo pesquero con una economía de turismo de surf en auge. Sigue siendo maravillosamente auténtico — de solo efectivo, caminos de tierra, territorio de 4×4 — mientras la comunidad afronta cuestiones reales de tenencia de la tierra, seguridad del agua y crecimiento sostenible para las próximas generaciones.",
    ],
  },
};

const OverviewES = (
  <>
    <p>
      Popoyo se encuentra en la costa del Pacífico del municipio de Tola, en el
      departamento de Rivas, Nicaragua. Antes un tramo remoto de caseríos pesqueros
      y tierras de cultivo, se ha convertido en uno de los destinos de surf más
      célebres de Centroamérica — famoso por un reef de clase mundial y por los
      vientos offshore, generados por el cercano Lago de Nicaragua, que peinan las
      olas casi todo el año.
    </p>

    <H3>Pesca, agricultura y raíces indígenas</H3>
    <p>
      Durante generaciones esta costa se definió por la pesca artesanal a pequeña
      escala y la agricultura de subsistencia. Las comunidades en torno al río
      Nahualapa y las salinas de Las Salinas vivían al ritmo de las mareas,
      lanzando pangas desde la arena y trabajando la tierra tierra adentro.
    </p>

    <H3>Revolución y reforma agraria</H3>
    <p>
      Tras la revolución de 1979, la tierra del sur de Tola se redistribuyó mediante
      la reforma agraria a cooperativas locales. Durante más de una década la costa
      siguió siendo rural y fuera del mapa, accesible solo por caminos de tierra.
    </p>

    <H3>El giro de 1990</H3>
    <p>
      La elección de Violeta Chamorro en 1990 y las políticas neoliberales que
      siguieron abrieron la puerta a la compra privada de tierras. Empezaron a
      llegar inversionistas extranjeros y surfistas viajeros, atraídos por olas
      perfectas y vacías.
    </p>

    <H3>El tsunami de 1992</H3>
    <p>
      La historia moderna de Popoyo quedó marcada por el tsunami del Pacífico de
      septiembre de 1992, que golpeó la costa nicaragüense y destruyó casi todo el
      pueblo original. Los sobrevivientes reconstruyeron en terreno más alto y
      seguro, al sur de Playa Sardinas.
    </p>

    <H3>El nacimiento del turismo de surf</H3>
    <p>
      El turismo de surf despegó a finales de los años noventa. La apertura en 1999
      del Popoyo Surf Lodge — el primer campamento de surf con todo incluido del
      país — puso la ola en el mapa internacional y desplazó la economía local de la
      pesca hacia los servicios.
    </p>

    <H3>Caminos, puentes y la Costa Esmeralda</H3>
    <p>
      La infraestructura de inicios de los 2000 — un puente sobre el río Nahualapa y
      nuevos caminos hacia la península de Guasacate — aceleró todo. Hoy Popoyo es el
      corazón de la “Costa Esmeralda”: Playa Popoyo al sur, Playa Guasacate al norte,
      y una sucesión de olas de clase mundial de Colorado y Panga Drops a El Astillero.
    </p>

    <H3>Popoyo hoy</H3>
    <p>
      Popoyo es hoy una mezcla vibrante de familias pescadoras nicaragüenses,
      surfistas, nómadas digitales y negocios de extranjeros. Sigue siendo
      refrescantemente auténtico — casi todo en efectivo, sin cajeros en el pueblo y
      con caminos de tierra que aún piden 4×4 — mientras equilibra la vida local con
      una economía turística en crecimiento.
    </p>

    <p className="italic text-[color:var(--ink-2)] border-l-2 border-[color:var(--coral)] pl-4 mt-4">
      “Donde los vientos offshore, las olas de clase mundial y una orgullosa costa pesquera se encuentran.”
    </p>
  </>
);

const OVERVIEW_BY_LANG = { es: OverviewES };

export default function HistorySection() {
  const { t, lang } = useI18n();
  return (
    <div data-testid="history-section" className="space-y-6">
      {/* Hero */}
      <section
        data-testid="history-hero"
        className="relative w-full overflow-hidden rounded-3xl"
        style={{
          backgroundImage: `url(${HERO_IMG})`,
          backgroundSize: "cover",
          backgroundPosition: "center 50%",
          minHeight: 320,
        }}
      >
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(180deg, rgba(27,59,111,0.55) 0%, rgba(27,59,111,0.45) 50%, rgba(27,59,111,0.85) 100%)",
          }}
        />
        <div className="relative px-6 sm:px-10 py-12 md:py-16">
          <p
            className="eyebrow mb-3"
            style={{ color: "#fff", textShadow: "0 1px 12px rgba(0,0,0,0.45)" }}
          >
            {t("history_eyebrow")}
          </p>
          <h1
            className="text-3xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-white max-w-4xl"
            style={{ fontFamily: "Outfit", textShadow: "0 2px 18px rgba(0,0,0,0.45)" }}
            data-testid="history-headline"
          >
            {t("history_title")}
          </h1>
          <p
            className="mt-3 text-sm sm:text-base lg:text-lg text-white/95 max-w-3xl"
            style={{ textShadow: "0 1px 14px rgba(0,0,0,0.45)" }}
          >
            {t("history_subtitle")}
          </p>
        </div>
      </section>

      {/* Table of contents */}
      <nav
        data-testid="history-toc"
        className="clay p-5 md:p-6"
        aria-label="Article index"
      >
        <p className="eyebrow mb-3">{t("history_in_archive")}</p>
        <ol className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1.5 text-sm">
          {ARTICLES.map((a, i) => {
            const Icon = a.icon;
            const localized = t(ARTICLE_TITLE_KEYS[a.id] || "");
            return (
              <li key={a.id}>
                <a
                  href={`#${a.id}`}
                  data-testid={`toc-${a.id}`}
                  className="inline-flex items-center gap-2 py-1 text-[color:var(--ink)] hover:text-[color:var(--coral)]"
                >
                  <span className="text-[color:var(--ink-2)] text-xs w-5 tabular-nums">{String(i + 1).padStart(2, "0")}</span>
                  <Icon className="w-4 h-4 text-[color:var(--coral)]" />
                  <span className="font-medium">{localized || a.title}</span>
                </a>
              </li>
            );
          })}
        </ol>
      </nav>

      {/* Archive photo slider — every historic image in one place, placed
          between the TOC and the long-form articles. */}
      <ArchiveGallery />

      {/* Articles */}
      {ARTICLES.map((a) => {
        const localizedBody = BODIES[lang]?.[a.id];
        const body = localizedBody || a.body;
        const overviewNode = a.id === "popoyo-overview"
          ? <OverviewRenderer data={OVERVIEW[lang] || OVERVIEW.en} />
          : null;
        if (a.id === "tsunami-1992") {
          return <TsunamiSpotlight key={a.id} lang={lang} image={PHOTOS.warehouseBay?.src} />;
        }
        return (
          <Article
            key={a.id}
            id={a.id}
            icon={a.icon}
            title={t(ARTICLE_TITLE_KEYS[a.id] || "") || a.title}
            photos={a.photos}
            photoPlaceholder={a.photoPlaceholder}
            customBody={overviewNode || a.customBody}
          >
            {body && body.map((p, i) => (
              <p key={i}>{p}</p>
            ))}
          </Article>
        );
      })}

      <p className="text-sm text-[color:var(--ink-2)] text-center pt-2">
        {t("history_more_soon")}
      </p>

      {/* Cross-promo to the sister app for San Juan del Sur */}
      <SjdsCrossPromo lang={lang} />
      <div className="mt-3">
        <DigitalNomadPromo lang={lang} />
      </div>

      {/* Decorative footer for visual closure on long pages */}
      <div className="flex justify-center pb-4">
        <Ship className="w-6 h-6 text-[color:var(--ink-2)]/60" />
      </div>
    </div>
  );
}
